import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { fetchCmsNewslettersPaged } from '../api/cmsNewsletters';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import { mapBulletinsFallbackFromI18n } from '../data/bulletins';
import {
    buildPdfDownloadFilename,
    downloadRemoteFile,
} from '../utils/downloadFile';

const PAGE_SIZE = 10;
const PAGE_QUERY_KEY = 'page';

/**
 * @param {{ statusLabel: string }} props
 */
function BulletinsPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <ul className="bulletins-grid list-unstyled mb-0">
                    {Array.from({ length: PAGE_SIZE }, (_, i) => (
                        <li
                            key={`bulletin-sk-${i}`}
                            className="bulletins-grid-item"
                            aria-hidden
                        >
                            <article className="bulletin-card bulletin-card--skeleton">
                                <div className="bulletin-card-top">
                                    <div className="bulletin-card-cover-frame">
                                        <Skeleton
                                            className="bulletin-card-skeleton-cover"
                                            borderRadius={6}
                                        />
                                    </div>
                                </div>
                                <div
                                    className="bulletin-card-rule"
                                    aria-hidden
                                />
                                <div className="bulletin-card-body">
                                    <Skeleton
                                        height={17}
                                        width="94%"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={17}
                                        width="72%"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={11}
                                        width="58%"
                                        borderRadius={4}
                                    />
                                    <div className="mt-auto pt-1 w-100">
                                        <Skeleton
                                            height={22}
                                            width={132}
                                            borderRadius={6}
                                        />
                                    </div>
                                </div>
                            </article>
                        </li>
                    ))}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

/**
 * Üyelere özel bülten arşivi — URL: /member-area/bulletins?page=2
 * Veri: CMS `Newsletter/GetAll` (sayfa başına 10).
 */
export function BulletinsPage() {
    const { t, i18n } = useTranslation();
    const { authToken } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState(
        /** @type {null | import('../data/bulletins').Bulletin[]} */ (null),
    );
    const [meta, setMeta] = useState(
        /** @type {null | { totalCount: number, totalPages: number, currentPage: number, pageSize: number, hasNext: boolean, hasPrevious: boolean }} */ (
            null
        ),
    );
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */
        (null),
    );
    const [downloadingPdfId, setDownloadingPdfId] = useState(
        /** @type {string | null} */ (null),
    );

    const handlePdfDownload = useCallback(
        async (bulletin) => {
            if (!bulletin?.pdfUrl || downloadingPdfId) return;

            setDownloadingPdfId(bulletin.id);
            try {
                await downloadRemoteFile(
                    bulletin.pdfUrl,
                    buildPdfDownloadFilename(bulletin.title),
                    { accessToken: authToken },
                );
            } catch (err) {
                const msg =
                    err instanceof Error
                        ? err.message
                        : t('bulletinsPage.pdfDownloadError');
                toast.error(msg || t('bulletinsPage.pdfDownloadError'));
            } finally {
                setDownloadingPdfId(null);
            }
        },
        [authToken, downloadingPdfId, t],
    );

    const fallbackItems = useMemo(() => {
        const raw = t('bulletinsPage.fallbackItems', { returnObjects: true });
        return mapBulletinsFallbackFromI18n(raw);
    }, [t]);

    const formatBulletinDate = useCallback(
        (createdAt) => {
            if (!createdAt || createdAt.length < 10) return null;
            const d = new Date(`${createdAt.slice(0, 10)}T12:00:00`);
            if (Number.isNaN(d.getTime())) return null;
            const loc = i18n.language?.toLowerCase().startsWith('en')
                ? 'en-US'
                : 'tr-TR';
            return new Intl.DateTimeFormat(loc, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(d);
        },
        [i18n.language],
    );

    const pageFromUrl = Math.max(
        1,
        parseInt(searchParams.get(PAGE_QUERY_KEY) || '1', 10) || 1,
    );

    const goToPage = useCallback(
        (nextPage) => {
            const p = Math.max(1, nextPage);
            if (p <= 1) {
                setSearchParams({}, { replace: true });
            } else {
                setSearchParams({ [PAGE_QUERY_KEY]: String(p) }, { replace: true });
            }
        },
        [setSearchParams],
    );

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const { items: nextItems, meta: nextMeta } =
                    await fetchCmsNewslettersPaged({
                        pageNumber: pageFromUrl,
                        pageSize: PAGE_SIZE,
                    });
                if (cancelled) return;
                setItems(nextItems);
                setMeta(nextMeta);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                const all = [...fallbackItems].sort((a, b) =>
                    b.createdAt.localeCompare(a.createdAt),
                );
                const totalPages = Math.max(
                    1,
                    Math.ceil(all.length / PAGE_SIZE),
                );
                const safePage = Math.min(pageFromUrl, totalPages);
                const start = (safePage - 1) * PAGE_SIZE;
                setItems(all.slice(start, start + PAGE_SIZE));
                setMeta({
                    totalCount: all.length,
                    totalPages,
                    currentPage: safePage,
                    pageSize: PAGE_SIZE,
                    hasNext: safePage < totalPages,
                    hasPrevious: safePage > 1,
                });
                setLoadError(
                    err instanceof Error
                        ? err.message
                        : t('bulletinsPage.genericLoadError'),
                );
                if (safePage !== pageFromUrl) {
                    goToPage(safePage);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [pageFromUrl, goToPage, fallbackItems, t]);

    useEffect(() => {
        if (meta && pageFromUrl > meta.totalPages && meta.totalPages >= 1) {
            goToPage(meta.totalPages);
        }
    }, [meta, pageFromUrl, goToPage]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pageFromUrl]);

    const showPagination =
        meta != null &&
        meta.totalPages > 1 &&
        items != null &&
        items.length > 0;

    return (
        <>
            <PageSeo
                title={`${t('bulletinsPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.bulletins')}
                noindex
            />
            <SubpageLayout
                title={t('bulletinsPage.title')}
                subtitle={t('bulletinsPage.subtitle')}
                contentClassName="bulletins-page"
            >
            {loadError ? (
                <p
                    className="bulletins-fallback-notice text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('bulletinsPage.fallbackNotice', { error: loadError })}
                </p>
            ) : null}
            {items === null ? (
                <BulletinsPageSkeleton
                    statusLabel={t('bulletinsPage.loading')}
                />
            ) : items.length === 0 ? (
                <p className="text-center text-secondary mb-0 py-5">
                    {t('bulletinsPage.empty')}
                </p>
            ) : (
                <>
                    <ul className="bulletins-grid list-unstyled mb-0">
                        {items.map((b) => {
                            const dateLabel = formatBulletinDate(b.createdAt);
                            return (
                                <li
                                    key={b.id}
                                    className="bulletins-grid-item"
                                >
                                    <article className="bulletin-card">
                                        <a
                                            href={b.pdfUrl}
                                            className="bulletin-card-cover-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={t(
                                                'bulletinsPage.openPdfAria',
                                                { title: b.title },
                                            )}
                                        >
                                            <div className="bulletin-card-top">
                                                <div className="bulletin-card-cover-frame">
                                                    <img
                                                        src={b.coverImageSrc}
                                                        alt=""
                                                        className="bulletin-card-cover"
                                                        width={480}
                                                        height={640}
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                </div>
                                            </div>
                                        </a>
                                        <div
                                            className="bulletin-card-rule"
                                            aria-hidden
                                        />
                                        <div className="bulletin-card-body">
                                            <h2 className="bulletin-card-title">
                                                {b.title}
                                            </h2>
                                            {dateLabel ? (
                                                <p className="bulletin-card-date-row">
                                                    <Calendar
                                                        size={15}
                                                        strokeWidth={1.85}
                                                        className="bulletin-card-date-icon flex-shrink-0"
                                                        aria-hidden
                                                    />
                                                    <time
                                                        dateTime={
                                                            b.createdAt.slice(
                                                                0,
                                                                10,
                                                            ) || undefined
                                                        }
                                                    >
                                                        {dateLabel}
                                                    </time>
                                                </p>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="bulletin-card-pdf-link"
                                                onClick={() =>
                                                    handlePdfDownload(b)
                                                }
                                                disabled={
                                                    downloadingPdfId === b.id
                                                }
                                            >
                                                {downloadingPdfId === b.id
                                                    ? t(
                                                          'bulletinsPage.pdfDownloading',
                                                      )
                                                    : t(
                                                          'bulletinsPage.pdfDownload',
                                                      )}
                                            </button>
                                        </div>
                                    </article>
                                </li>
                            );
                        })}
                    </ul>
                    {showPagination && meta ? (
                        <nav
                            className="news-list-pagination d-flex flex-column align-items-center gap-3 mt-4 pt-2"
                            aria-label={t('bulletinsPage.paginationNavAria')}
                        >
                            <p className="news-list-pagination-summary mb-0">
                                {t('bulletinsPage.paginationSummary', {
                                    count: meta.totalCount,
                                    current: meta.currentPage,
                                    totalPages: meta.totalPages,
                                })}
                            </p>
                            <ul className="news-list-pagination-pages list-unstyled mb-0 d-flex flex-wrap align-items-center justify-content-center gap-1 gap-sm-2">
                                <li>
                                    <button
                                        type="button"
                                        className="news-list-pagination-btn news-list-pagination-btn--nav"
                                        disabled={!meta.hasPrevious}
                                        onClick={() =>
                                            goToPage(meta.currentPage - 1)
                                        }
                                        aria-label={t(
                                            'bulletinsPage.paginationPrevAria',
                                        )}
                                    >
                                        <ChevronLeft
                                            size={22}
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    </button>
                                </li>
                                {meta.totalPages <= 12
                                    ? Array.from(
                                          { length: meta.totalPages },
                                          (_, i) => i + 1,
                                      ).map((n) => (
                                          <li key={n}>
                                              <button
                                                  type="button"
                                                  className={`news-list-pagination-btn${n === meta.currentPage ? ' is-active' : ''}`}
                                                  onClick={() => goToPage(n)}
                                                  aria-label={t(
                                                      'bulletinsPage.paginationPageAria',
                                                      { page: n },
                                                  )}
                                                  aria-current={
                                                      n === meta.currentPage
                                                          ? 'page'
                                                          : undefined
                                                  }
                                              >
                                                  {n}
                                              </button>
                                          </li>
                                      ))
                                    : null}
                                {meta.totalPages > 12 ? (
                                    <li>
                                        <span
                                            className="news-list-pagination-indicator"
                                            aria-current="page"
                                        >
                                            {meta.currentPage} /{' '}
                                            {meta.totalPages}
                                        </span>
                                    </li>
                                ) : null}
                                <li>
                                    <button
                                        type="button"
                                        className="news-list-pagination-btn news-list-pagination-btn--nav"
                                        disabled={!meta.hasNext}
                                        onClick={() =>
                                            goToPage(meta.currentPage + 1)
                                        }
                                        aria-label={t(
                                            'bulletinsPage.paginationNextAria',
                                        )}
                                    >
                                        <ChevronRight
                                            size={22}
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    ) : null}
                </>
            )}
        </SubpageLayout>
        </>
    );
}
